/**
 * Conversión celda ↔ valor.
 *
 * Dos decisiones que existen porque el usuario va a editar la planilla a mano:
 *
 * 1. **Las filas se leen por nombre de cabecera, no por posición.** Si alguien mueve una
 *    columna, inserta una al medio o agrega una propia, la app sigue funcionando. Leer por
 *    índice fijo convertiría cualquier reordenamiento en datos corruptos silenciosos, que en
 *    una tabla de pesos corporales es exactamente lo que no puede pasar.
 * 2. **Los números se escriben con punto y se leen con punto O coma.** Se escribe con punto
 *    porque la planilla se crea en locale en_US y así queda como número de verdad; se lee
 *    tolerante porque una persona escribiendo "97,5" a mano no está cometiendo un error.
 */

import type { Columna, DefinicionTab } from './esquema.ts';

const VERDADEROS = new Set(['sí', 'si', 'true', 'verdadero', '1', 'x', 'yes', 'y']);
const FALSOS = new Set(['no', 'false', 'falso', '0', '', 'n']);

export function aCelda(valor: unknown, col: Columna): string {
  if (valor === null || valor === undefined) return '';

  switch (col.tipo) {
    case 'booleano':
      return valor ? 'sí' : 'no';
    case 'lista':
      return Array.isArray(valor) ? valor.map(String).join('|') : String(valor);
    case 'numero':
    case 'entero': {
      const n = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));
      if (!Number.isFinite(n)) return '';
      // Sin notación científica ni comas: la celda tiene que ser legible y parseable.
      return col.tipo === 'entero' ? String(Math.round(n)) : String(n);
    }
    default:
      return String(valor);
  }
}

export function deCelda(celda: string | undefined, col: Columna): unknown {
  const s = (celda ?? '').trim();

  switch (col.tipo) {
    case 'booleano': {
      const l = s.toLowerCase();
      if (VERDADEROS.has(l)) return true;
      if (FALSOS.has(l)) return false;
      // Un valor que no se entiende no se interpreta como false: se dice que no se sabe.
      return col.nulable ? null : false;
    }
    case 'lista':
      return s === '' ? [] : s.split('|').map((x) => x.trim()).filter((x) => x !== '');
    case 'numero':
    case 'entero': {
      if (s === '') return col.nulable ? null : 0;
      const n = Number(s.replace(/\s/g, '').replace(',', '.'));
      if (!Number.isFinite(n)) return col.nulable ? null : 0;
      return col.tipo === 'entero' ? Math.round(n) : n;
    }
    default:
      if (s === '' && col.nulable) return null;
      return s;
  }
}

export function cabeceraEsperada(tab: DefinicionTab): string[] {
  return tab.columnas.map((c) => c.nombre);
}

/** Mapa nombre de columna → índice, según la cabecera REAL de la planilla. */
export function indiceCabecera(cabeceraReal: string[]): Map<string, number> {
  const m = new Map<string, number>();
  cabeceraReal.forEach((nombre, i) => {
    const limpio = (nombre ?? '').trim();
    if (limpio !== '' && !m.has(limpio)) m.set(limpio, i);
  });
  return m;
}

export interface ProblemaCabecera {
  faltantes: string[];
  extra: string[];
}

/** Columnas del esquema que no están en la planilla, y columnas de la planilla que no conocemos. */
export function revisarCabecera(tab: DefinicionTab, cabeceraReal: string[]): ProblemaCabecera {
  const real = indiceCabecera(cabeceraReal);
  const esperadas = new Set(cabeceraEsperada(tab));
  return {
    faltantes: cabeceraEsperada(tab).filter((n) => !real.has(n)),
    extra: [...real.keys()].filter((n) => !esperadas.has(n)),
  };
}

export function deFila(
  fila: string[],
  tab: DefinicionTab,
  indice: Map<string, number>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const col of tab.columnas) {
    const i = indice.get(col.nombre);
    out[col.nombre] = deCelda(i === undefined ? '' : fila[i], col);
  }
  return out;
}

/**
 * Valores en el orden REAL de la planilla.
 *
 * `filaPrevia` es el contenido actual de esa fila, y es obligatorio pasarlo al actualizar:
 * las columnas que el usuario agregó por su cuenta —una nota, una fórmula, un color -
 * conservan su valor. Sin esto, cada corrección de un peso borraría en silencio las
 * anotaciones de la persona, en la app cuya razón de ser es que la planilla se pueda editar
 * a mano.
 */
export function aFila(
  obj: Record<string, unknown>,
  tab: DefinicionTab,
  cabeceraReal: string[],
  filaPrevia?: string[],
): string[] {
  const porNombre = new Map(tab.columnas.map((c) => [c.nombre, c]));
  return cabeceraReal.map((nombre, i) => {
    const col = porNombre.get((nombre ?? '').trim());
    if (col) return aCelda(obj[col.nombre], col);
    return filaPrevia?.[i] ?? '';
  });
}

/**
 * Forma canónica de los campos que la app conoce, para detectar si una fila cambió de verdad.
 * Ignora las columnas ajenas: que el usuario escriba una nota no es un cambio que haya que
 * sincronizar de vuelta.
 */
export function firmaFila(obj: Record<string, unknown>, tab: DefinicionTab): string {
  return tab.columnas.map((c) => aCelda(obj[c.nombre], c)).join('');
}

export interface TablaLeida<T> {
  filas: T[];
  /** Número de fila en la planilla (1-based, contando la cabecera) por cada elemento. */
  numerosDeFila: number[];
  /** La fila cruda tal como está en la planilla, para poder preservar columnas ajenas. */
  crudas: string[][];
  cabecera: string[];
  problemas: ProblemaCabecera;
}

/**
 * Convierte la respuesta cruda de una pestaña. La primera fila es la cabecera. Se saltean
 * las filas totalmente vacías, que aparecen todo el tiempo cuando alguien borra contenido
 * con Supr en vez de eliminar la fila.
 */
export function leerTabla<T>(valores: string[][], tab: DefinicionTab): TablaLeida<T> {
  const cabecera = valores[0] ?? cabeceraEsperada(tab);
  const indice = indiceCabecera(cabecera);
  const problemas = revisarCabecera(tab, cabecera);

  const filas: T[] = [];
  const numerosDeFila: number[] = [];
  const crudas: string[][] = [];

  for (let i = 1; i < valores.length; i++) {
    const fila = valores[i] ?? [];
    if (fila.every((c) => (c ?? '').trim() === '')) continue;
    filas.push(deFila(fila, tab, indice) as T);
    numerosDeFila.push(i + 1); // 1-based, y la fila 1 es la cabecera
    crudas.push(fila);
  }

  return { filas, numerosDeFila, crudas, cabecera, problemas };
}

/** Notación A1 de una columna: 0 → A, 25 → Z, 26 → AA. */
export function letraColumna(indice: number): string {
  let n = indice + 1;
  let out = '';
  while (n > 0) {
    const resto = (n - 1) % 26;
    out = String.fromCharCode(65 + resto) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

export function rangoTab(tab: string): string {
  return `'${tab.replaceAll("'", "''")}'`;
}

export function rangoColumna(tab: string, indiceColumna: number): string {
  const l = letraColumna(indiceColumna);
  return `${rangoTab(tab)}!${l}:${l}`;
}

export function rangoFila(tab: string, fila: number, anchoColumnas: number): string {
  return `${rangoTab(tab)}!A${fila}:${letraColumna(anchoColumnas - 1)}${fila}`;
}
